<template>
    <div
        v-if="categories"
        class="bug-reporting"
    >
        <div class="content">
            <div class="category-container">
                <div class="caption">Vyberte téma</div>
                <div
                    class="radiobuttons"
                    :class="{ invalid: !validation.activeCategory }"
                >
                    <div
                        v-for="item in categories"
                        :key="item.ID"
                        class="input-item"
                    >
                        <input
                            :id="item.ID.toString()"
                            v-model="activeCategory"
                            type="radio"
                            name="radiobuttons"
                            :value="item.ID"
                        />
                        <label :for="item.ID.toString()">
                            {{ item.NAZEV }}
                        </label>
                    </div>
                    <span class="error-message">
                        Vyberte kategorii problému
                    </span>
                </div>
            </div>
            <div class="input-container">
                <div
                    class="input-item"
                    :class="{ invalid: !validation.email }"
                >
                    <span class="caption">E-mail:</span><br />
                    <input
                        v-model="email"
                        type="email"
                        class="w-full border bg-white p-2 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        size="30"
                        required
                    />
                    <span class="error-message">
                        Zadejte email ve správném tvaru
                    </span>
                </div>
                <div
                    class="input-item"
                    :class="{ invalid: !validation.description }"
                >
                    <span class="caption"> Vaše zpráva </span>
                    <br />
                    <textarea
                        v-model="description"
                        rows="4"
                        class="min-h-[154px] w-full border bg-white p-2 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        placeholder="Popište, prosím, co nejpodrobněji Váš problém (max 500 znaků)"
                        required
                    />
                    <span class="error-message">
                        Nevyhovující délka popisu (min 10 znaků, max 500 znaků)
                    </span>
                </div>
            </div>
        </div>
        <div class="submit-wrapper">
            <nuxt-turnstile
                v-if="mounted"
                ref="captcha"
                v-model="token"
                class="captcha"
            />
            <div
                class="link-btn"
                @click="onSubmit"
            >
                Odeslat
            </div>
        </div>
        <div
            v-if="success"
            class="success-text"
        >
            Vaše zpráva byla odeslána.
        </div>
    </div>
</template>
<script setup lang="ts">
import { z } from "zod"

const store = useMainStore()
const route = useRoute()

interface KontaktChybyItem {
    ID: number
    NAZEV: string
    EMAILS: string
}

const { serverUri } = useDataLogic()

const mounted = ref(false)
const activeCategory = ref(0)

onMounted(() => {
    mounted.value = true
})

onUnmounted(() => {
    mounted.value = false
})

const { data: categories } = await useLazyFetch<KontaktChybyItem[]>(
    "https://app.iprpraha.cz/apl/app/prazsky-ulicnik/v_kontaktchyby.json",
    {
        baseURL: import.meta.client ? store.apiBaseUrl : store.apiBaseUrlInt,
        onResponse: ({ response }) => {
            console.log("Categories fetched:", response._data)

            if (route.query.problem) {
                const problem = route.query.problem as string
                console.log("Problem from route query:", problem)

                const category = response._data?.find(
                    (el: KontaktChybyItem) => el.NAZEV === problem
                )
                if (category) {
                    console.log("Setting active category:", category)

                    activeCategory.value = category.ID
                }
            }
            if (route.query.url) {
                const url = route.query.url as string
                console.log("URL from route query:", url)

                description.value = `Chyba se vyskytla na adrese: ${url}`
            }
        },
    }
)

const captcha = ref()

const activeCategoryName = ref("")
const activeCategoryEmails = ref("")

const email = ref("")
const description = ref("")

const success = ref(false)

watch(activeCategory, newValue => {
    if (newValue === 0) {
        activeCategoryName.value = ""
        activeCategoryEmails.value = ""
    } else {
        const categoryItem = categories.value?.find(el => el.ID === newValue)
        if (categoryItem) {
            activeCategoryName.value = categoryItem.NAZEV
            activeCategoryEmails.value = categoryItem.EMAILS
        }
    }
})

const validation = reactive<{
    [k: string]: boolean
}>({
    email: true,
    description: true,
    activeCategory: true,
})

const token = ref("")

async function onSubmit() {
    const isValidated = checkValidity()
    if (isValidated) {
        await captcha.value?.reset()
        const response = await $fetch("/api/verify", {
            method: "POST",
            body: {
                token: token.value,
            },
        })

        if (response.success) {
            interface SubmitResponse {
                success: boolean
                [key: string]: any
            }

            const submitRes = await $fetch<SubmitResponse>("/send-html", {
                baseURL: serverUri(),
                method: "POST",
                body: {
                    domain: "prazskyulicnik",
                    emails: activeCategoryEmails.value,
                    html: `E-mail: ${email.value}, br;eak Téma: ${activeCategoryName.value},  br;eak Vaše zpráva: ${description.value}`,
                    subject:
                        "Pražský uličník - dotazy, podněty, hlášení chyb v obsahu, funkčnost aplikace",
                },
            })

            setTimeout(() => {
                email.value = ""
                description.value = ""
            }, 2000)

            if (submitRes) {
                success.value = true
                setTimeout(() => {
                    success.value = false
                }, 2000)
            }
        }
    }
}

const MIN_DESCRIPTION_LENGTH = 10
const MAX_DESCRIPTION_LENGTH = 500

function checkValidity(): boolean {
    validation.email = z.string().email().min(5).safeParse(email.value).success
    validation.description = z
        .string()
        .min(MIN_DESCRIPTION_LENGTH)
        .max(MAX_DESCRIPTION_LENGTH)
        .optional()
        .safeParse(description.value).success
    validation.activeCategory = z
        .number()
        .gt(0)
        .safeParse(activeCategory.value).success

    const isValidated = Object.keys(validation).every(k => validation[k])
    return isValidated
}
</script>

<style lang="scss" scoped></style>
